import invariant from "tiny-invariant";
import JSBI from "jsbi";
import { Fraction, MaxUint256 } from "@uniswap/sdk-core";
import { FungibleToken } from "../../models/financial/fungible_token.js";
import { CurrencyAmount } from "../../models/financial/currency_amount.js";
import { rawAmountToDecimalString } from "../../utils/format_utils.js";
export class FungibleTokenAmount extends Fraction {
    /**
     * Returns a new fungibleToken amount instance from the unitless amount of token, i.e. the raw amount
     * @param fungibleToken the fungibleToken in the amount
     * @param rawAmount the raw token or ether amount
     */
    static fromRawAmount(fungibleToken, rawAmount, amountInFiatCurrency) {
        return new FungibleTokenAmount(fungibleToken, rawAmount, undefined, amountInFiatCurrency);
    }
    /**
     * Construct a fungibleToken amount with a denominator that is not equal to 1
     * @param fungibleToken the fungibleToken
     * @param numerator the numerator of the fractional token amount
     * @param denominator the denominator of the fractional token amount
     */
    static fromFractionalAmount(fungibleToken, numerator, denominator, amountInFiatCurrency) {
        return new FungibleTokenAmount(fungibleToken, numerator, denominator, amountInFiatCurrency);
    }
    static toFungibleTokenAmount(amount) {
        if (!amount) {
            return undefined;
        }
        const amountInFiatCurrency = CurrencyAmount.toCurrencyAmount(amount.amountInFiatCurrency);
        return FungibleTokenAmount.fromRawAmount(FungibleToken.toFungibleToken(amount.token), amount.value, amountInFiatCurrency);
    }
    constructor(fungibleToken, numerator, denominator, amountInFiatCurrency) {
        super(numerator.toString(), denominator?.toString());
        invariant(JSBI.lessThanOrEqual(this.quotient, MaxUint256), "AMOUNT");
        this.token = fungibleToken;
        this.decimalScale = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(fungibleToken.decimals));
        this.amountInFiatCurrency = amountInFiatCurrency;
    }
    equals(other) {
        return this.token.equals(other?.token) && this.toRawAmount() == other?.toRawAmount();
    }
    add(other) {
        invariant(this.token.equals(other.token), "FUNGIBLE_TOKEN");
        const added = super.add(other);
        return FungibleTokenAmount.fromFractionalAmount(this.token, added.numerator, added.denominator);
    }
    subtract(other) {
        invariant(this.token.equals(other.token), "FUNGIBLE_TOKEN");
        const rawResult = this.toRawAmount() - other.toRawAmount();
        return FungibleTokenAmount.fromRawAmount(this.token, rawResult);
    }
    multiply(other) {
        const multiplied = super.multiply(other);
        return FungibleTokenAmount.fromFractionalAmount(this.token, multiplied.numerator, multiplied.denominator);
    }
    divide(other) {
        const divided = super.divide(other);
        return FungibleTokenAmount.fromFractionalAmount(this.token, divided.numerator, divided.denominator);
    }
    scale(other) {
        const scaled = super.multiply(other);
        return FungibleTokenAmount.fromFractionalAmount(this.token, scaled.numerator, scaled.denominator);
    }
    greaterThanOrEqual(other) {
        return this.greaterThan(other) || this.equals(other);
    }
    toRawAmount() {
        return BigInt(this.quotient.toString());
    }
    /**
     * Returns the amount as a decimal string, accounting for the token's decimals.
     * Uses string manipulation to avoid precision loss with large numbers.
     * e.g., raw amount 1000000 with 6 decimals returns "1.000000"
     * @param decimalPlaces Number of decimal places to show (defaults to token decimals)
     */
    toDecimalString(decimalPlaces) {
        return rawAmountToDecimalString(this.toRawAmount(), this.token.decimals, decimalPlaces);
    }
    fiatValue() {
        return this.amountInFiatCurrency;
    }
    toCurrencyValue(price) {
        const fraction = price.multiply(this);
        const denominator = JSBI.multiply(fraction.denominator, this.decimalScale);
        this.amountInFiatCurrency = CurrencyAmount.fromFractionalAmount(price.currency, fraction.numerator, denominator);
        return this.amountInFiatCurrency;
    }
    async toTokenCurrencyValue(price) {
        if (price) {
            return this.toCurrencyValue(price);
        }
        // TODO(felix): rethink this
        // price = (await DataProvider.getTokenPrice(this.token)) as CurrencyAmount;
        // return this.toCurrencyValue(price);
    }
    static smallUnitPriceInDecimal(token, price, decimal) {
        if (decimal < price.currency.decimals) {
            return undefined;
        }
        const decimalScale = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(decimal));
        const tokenDecimalScale = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(token.decimals));
        const multiplier = JSBI.divide(decimalScale, tokenDecimalScale);
        const priceInDecimal = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(price.currency.decimals));
        return price.multiply(multiplier).divide(priceInDecimal);
    }
    /**
     * Used to calculate the amount of token B that one can get for a given amount of token A.
     * aPrice is the unit price of token A in fiat currency
     * bPrice is the unit price of token B in fiat currency
     */
    static exchange(a, aPrice, bPrice, b) {
        invariant(aPrice.currency.equals(bPrice.currency), "CURRENCY");
        const ratio = aPrice.divide(bPrice);
        const numberOfB = ratio.multiply(a);
        const numerator = JSBI.multiply(numberOfB.numerator, JSBI.BigInt(10 ** b.decimals));
        const denominator = JSBI.multiply(numberOfB.denominator, a.decimalScale);
        const result = FungibleTokenAmount.fromFractionalAmount(b, numerator, denominator);
        return result;
    }
    /**
     * Exchanges an amount of token A for token B.
     * If both tokens have the same coinGeckoId (similar tokens), uses 1:1 exchange adjusted for decimals.
     * Otherwise, uses provided prices or returns undefined if prices aren't available.
     */
    static async exchangeTokens(amountA, targetToken, priceA, priceB) {
        // If same token, return a clone
        if (amountA.token.equals(targetToken)) {
            return FungibleTokenAmount.fromRawAmount(targetToken, amountA.toRawAmount());
        }
        // If similar tokens (same coinGeckoId), convert using 1:1 ratio adjusted for decimals
        if (amountA.token.coinGeckoId && amountA.token.coinGeckoId === targetToken.coinGeckoId) {
            const decimalDiff = targetToken.decimals - amountA.token.decimals;
            let rawAmount;
            if (decimalDiff >= 0) {
                rawAmount = amountA.toRawAmount() * BigInt(10 ** decimalDiff);
            }
            else {
                rawAmount = amountA.toRawAmount() / BigInt(10 ** -decimalDiff);
            }
            return FungibleTokenAmount.fromRawAmount(targetToken, rawAmount);
        }
        // If prices are provided, use them
        if (priceA && priceB) {
            return FungibleTokenAmount.exchange(amountA, priceA, priceB, targetToken);
        }
        // Cannot exchange without prices for non-similar tokens
        return undefined;
    }
    /**
     * Adds two amounts of similar tokens (tokens with the same coinGeckoId).
     * The result is in terms of the first token (amountA's token).
     */
    static addSimilarTokens(amountA, amountB) {
        // If same token, just add
        if (amountA.token.equals(amountB.token)) {
            return amountA.add(amountB);
        }
        // Check if tokens are similar (same coinGeckoId)
        invariant((amountA.token.coinGeckoId && amountA.token.coinGeckoId === amountB.token.coinGeckoId) ||
            (amountA.token.isStablecoin && amountB.token.isStablecoin), "SIMILAR_TOKENS");
        // Convert amountB to amountA's token using 1:1 ratio adjusted for decimals
        const decimalDiff = amountA.token.decimals - amountB.token.decimals;
        let convertedRawAmount;
        if (decimalDiff >= 0) {
            convertedRawAmount = amountB.toRawAmount() * BigInt(10 ** decimalDiff);
        }
        else {
            convertedRawAmount = amountB.toRawAmount() / BigInt(10 ** -decimalDiff);
        }
        const convertedB = FungibleTokenAmount.fromRawAmount(amountA.token, convertedRawAmount);
        return amountA.add(convertedB);
    }
    /**
     * Calculates the difference of amountB from amountA (amountA - amountB).
     * If tokens are different, converts amountB to amountA's token first.
     */
    static async differenceOfBFromA(amountA, amountB) {
        // If same token, just subtract
        if (amountA.token.equals(amountB.token)) {
            const difference = amountA.subtract(amountB);
            return difference;
        }
        // Convert amountB to amountA's token
        const convertedB = await FungibleTokenAmount.exchangeTokens(amountB, amountA.token);
        if (!convertedB) {
            return undefined;
        }
        return amountA.subtract(convertedB);
    }
    cloneWithAmount(amount) {
        return FungibleTokenAmount.fromRawAmount(this.token, amount);
    }
    toFungibleTokenAmountModel() {
        return {
            token: this.token.toFungibleTokenModel(),
            value: this.toRawAmount(),
            amount: this.toRawAmount(),
            amountInFiatCurrency: this.amountInFiatCurrency?.toCurrencyAmountModel(),
        };
    }
}
